import React, { createContext, useCallback, useContext, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';

/**
 * Promise-based confirm dialog — a drop-in replacement for `window.confirm()`
 * that renders a proper MUI Dialog. See the website twin under
 * `English-Teacher-Website/src/Components/Shared/ConfirmDialog.jsx` for
 * the full usage docs. Behaviour is intentionally identical across apps.
 */
const ConfirmContext = createContext(null);

export const ConfirmProvider = ({ children }) => {
    const [state, setState] = useState(null);

    const confirm = useCallback((opts) => {
        const options = typeof opts === 'string' ? { message: opts } : (opts || {});
        return new Promise((resolve) => {
            setState((prev) => {
                if (prev?.resolve) prev.resolve(false);
                return {
                    title: options.title || 'Are you sure?',
                    message: options.message || '',
                    confirmLabel: options.confirmLabel || 'Confirm',
                    cancelLabel: options.cancelLabel || 'Cancel',
                    danger: !!options.danger,
                    resolve,
                };
            });
        });
    }, []);

    const close = (value) => {
        if (state?.resolve) state.resolve(value);
        setState(null);
    };

    return (
        <ConfirmContext.Provider value={confirm}>
            {children}
            <Dialog open={!!state} onClose={() => close(false)} maxWidth="xs" fullWidth>
                <DialogTitle>{state?.title}</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ whiteSpace: 'pre-wrap' }}>
                        {state?.message}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => close(false)}>{state?.cancelLabel}</Button>
                    <Button
                        onClick={() => close(true)}
                        variant="contained"
                        color={state?.danger ? 'error' : 'primary'}
                        autoFocus
                    >
                        {state?.confirmLabel}
                    </Button>
                </DialogActions>
            </Dialog>
        </ConfirmContext.Provider>
    );
};

export const useConfirm = () => {
    const ctx = useContext(ConfirmContext);
    if (!ctx) throw new Error('useConfirm must be used inside <ConfirmProvider>');
    return ctx;
};
